/*
 * Per-database filter configuration
 * ─────────────────────────────────
 * Different databases have completely different schemas (Brokers uses 8
 * Yes/No capability columns for "class of trade" while Retailers has a
 * single `channel` string). Automatic distinct-value detection can't handle
 * that — so we hand-curate the relevant filters per slug.
 *
 * Filter types:
 *   • select        — `<select>` dropdown; user picks one of the distinct
 *                     values found in the column, exact-match.
 *   • autocomplete  — free-text input with a datalist of distinct values;
 *                     exact-match (used for large-cardinality fields like
 *                     "HQ city" where a dropdown would be unwieldy).
 *   • contains      — free-text input; case-insensitive substring match
 *                     across the column (e.g. "capabilities" text tag lists).
 *   • capability    — a virtual "OR-of-columns" filter. Each option maps to
 *                     a Yes/No column. When ANY option is picked, the row
 *                     is kept if the corresponding column is truthy
 *                     (i.e. not empty, not "No", not "None").
 *
 * Groups (capability filters) render as a single label with a chip strip
 * of option pills the user can toggle.
 *
 * The `distinctValues` field is inferred at runtime by scanning the loaded
 * rows — nothing here needs manual updates when data changes.
 */
export const DATABASE_FILTERS = {
  "brokers": {
    filters: [
      { id: "region", label: "Region", type: "select", column: "region" },
      { id: "inter_national", label: "International reach", type: "select", column: "inter_national" },
    ],
    groups: [
      {
        id: "class_of_trade",
        label: "Class of Trade",
        options: [
          { label: "Natural / Organic", column: "natural_organic" },
          { label: "Conventional", column: "conventional" },
          { label: "Mass", column: "mass" },
          { label: "Drug / C-Store", column: "drug_c_store" },
          { label: "Club", column: "club" },
          { label: "E-Comm", column: "e_comm" },
          { label: "Foodservice", column: "foodservice_other_office_military_cafe_etc" },
        ],
      },
    ],
  },

  "distributors": {
    filters: [
      { id: "region", label: "Region", type: "select", column: "region" },
      { id: "headquarters", label: "Headquarters", type: "autocomplete", column: "headquarters" },
    ],
    groups: [
      {
        id: "channel",
        label: "Channel",
        options: [
          { label: "Grocery", column: "grocery" },
          { label: "Ambient / Dry", column: "ambient_dry" },
          { label: "Refrigerated", column: "refrig_erated" },
          { label: "Frozen", column: "frozen" },
          { label: "Foodservice", column: "foodservice" },
          { label: "Convenience", column: "convenience_store" },
          { label: "Natural / Organic", column: "natural_organic" },
          { label: "E-Comm", column: "e_comm" },
        ],
      },
    ],
  },

  "product-development": {
    filters: [
      { id: "type", label: "Type", type: "select", column: "type" },
      { id: "typical_customer_size", label: "Customer size", type: "select", column: "typical_customer_size" },
      { id: "location", label: "Location / State", type: "autocomplete", column: "location" },
      { id: "capabilities", label: "Capabilities", type: "contains", column: "capabilities" },
      { id: "product_specialties", label: "Product specialties", type: "contains", column: "product_specialties" },
    ],
  },

  "retailers": {
    filters: [
      { id: "channel", label: "Channel", type: "select", column: "channel" },
      { id: "hq_region", label: "HQ region", type: "select", column: "hq_region" },
      { id: "hq_state", label: "HQ state", type: "autocomplete", column: "hq_state" },
      { id: "available_programs", label: "Programs", type: "select", column: "available_programs" },
      { id: "calendar_review_or_open", label: "Review type", type: "select", column: "calendar_review_or_open" },
    ],
  },

  "investors": {
    filters: [
      { id: "title", label: "Title", type: "contains", column: "title" },
      { id: "contact_city", label: "Contact city", type: "autocomplete", column: "contact_city" },
      { id: "contact_state", label: "Contact state", type: "autocomplete", column: "contact_state" },
      { id: "contact_country", label: "Contact country", type: "autocomplete", column: "contact_country" },
    ],
  },

  "co-packers": {
    filters: [
      { id: "state", label: "State", type: "autocomplete", column: "state" },
      { id: "city", label: "City", type: "autocomplete", column: "city" },
      { id: "minimum_run", label: "Minimum", type: "autocomplete", column: "minimum_run" },
      { id: "formats", label: "Formats", type: "contains", column: "formats" },
    ],
  },

  "excess-inventory": {
    filters: [
      { id: "type", label: "Type", type: "select", column: "type" },
      { id: "foodservice_retail_both", label: "Channel", type: "select", column: "foodservice_retail_both" },
      { id: "focus_area_or_location", label: "Focus area", type: "autocomplete", column: "focus_area_or_location" },
      { id: "city", label: "City", type: "autocomplete", column: "city" },
    ],
  },

  "financing": {
    filters: [
      { id: "rank", label: "Rank", type: "select", column: "rank" },
    ],
    groups: [
      {
        id: "services",
        label: "Services offered",
        options: [
          { label: "Accounts receivable", column: "accounts_receivable" },
          { label: "SBA term loan", column: "sba_term_loan" },
          { label: "Unsecured loan", column: "unsecured_loan" },
          { label: "Purchase order", column: "purchase_order" },
          { label: "Inventory", column: "inventory" },
          { label: "Equipment financing", column: "equipment_financing" },
          { label: "Factoring", column: "factoring" },
          { label: "Venture debt", column: "venture_debt" },
        ],
      },
    ],
  },

  "freight-logistics": {
    filters: [
      { id: "type", label: "Type", type: "select", column: "type" },
      { id: "region", label: "Region", type: "select", column: "region" },
      { id: "speciality", label: "Specialty", type: "select", column: "speciality" },
      { id: "temperature_controlled", label: "Temp controlled", type: "select", column: "temperature_controlled" },
    ],
    groups: [
      {
        id: "modes",
        label: "Modes",
        options: [
          { label: "Air", column: "air" },
          { label: "Ocean", column: "ocean" },
          { label: "Cross-border", column: "cross_border" },
        ],
      },
    ],
  },
};

/* Truthiness check for capability columns — the same "Yes/No" cell can arrive
 * as "Yes", "yes", "YES", "true", "1", etc., and blank / "No" / "None" mean
 * the capability is absent. */
export function isCapabilityTruthy(v) {
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  if (!s) return false;
  if (["no", "n", "none", "false", "0", "-", "n/a", "na"].includes(s)) return false;
  return true;
}

/*
 * Per-database column-width overrides for the Data table
 * ──────────────────────────────────────────────────────
 * Each entry maps `column key → { min, max }` as Tailwind arbitrary-value
 * classes. Min width is applied to the inner wrapper so the table's
 * automatic column-sizing algorithm grows the column at least that wide
 * (browsers only grow a column past content width when a cell declares a
 * larger intrinsic width). Max width is applied to the <td> so very long
 * single tokens still wrap instead of blowing out the row.
 *
 * Content never truncates with ellipsis anymore (per user request); it
 * wraps with `break-words whitespace-normal` so long values spill onto
 * multiple lines rather than being hidden.
 */
export const DATABASE_COLUMN_WIDTHS = {
  "investors": {
    website:        { min: "min-w-[280px]", max: "max-w-[400px]" },
    li_profile_url: { min: "min-w-[280px]", max: "max-w-[400px]" },
    company:        { min: "min-w-[220px]", max: "max-w-[320px]" },
  },
};

/** Resolve the Tailwind width classes for a given (slug, column). */
export function columnWidthClass(slug, column) {
  const bySlug = DATABASE_COLUMN_WIDTHS[slug] || {};
  const cfg = bySlug[column];
  if (!cfg) return { td: "max-w-[260px]", inner: "" };
  return { td: cfg.max || "max-w-[260px]", inner: cfg.min || "" };
}

// Display label for a database column key, in Title Case
// ("major_clients_(food_&_bev)" → "Major Clients (Food & Bev)").
const LABEL_ACRONYMS = {
  ltl: "LTL", ftl: "FTL", url: "URL", hq: "HQ", bbb: "BBB", fda: "FDA", ftc: "FTC",
  usda: "USDA", sba: "SBA", cpg: "CPG", niq: "NIQ", li: "LI", id: "ID", linkedin: "LinkedIn",
};
const LABEL_SMALL_WORDS = new Set(["and", "or", "of", "the", "to", "for", "in", "a", "an", "at", "by", "on"]);

export function columnLabel(key) {
  const text = String(key || "")
    .replace(/_/g, " ")
    .replace(/linked in/gi, "linkedin")
    .replace(/^personlinkedin$/i, "person linkedin")
    .replace(/refrig-erated/gi, "refrigerated")
    .replace(/ph:no/gi, "phone no")
    .replace(/\s*\/\s*/g, " / ")
    .replace(/\s+/g, " ")
    .trim();
  let first = true;
  return text.replace(/[A-Za-z][A-Za-z']*/g, (word, offset) => {
    const lower = word.toLowerCase();
    const isFirst = first;
    first = false;
    if (LABEL_ACRONYMS[lower]) return LABEL_ACRONYMS[lower];
    const afterHyphen = offset > 0 && text[offset - 1] === "-";
    if (!isFirst && !afterHyphen && LABEL_SMALL_WORDS.has(lower)) return lower;
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  });
}

// Build a page filter config from admin-selected columns. Columns with a
// small set of short values get a dropdown; the rest a "contains" box.
export function buildFilterConfig(filterColumns, rows) {
  return {
    filters: filterColumns.map((col) => {
      const seen = new Set();
      let long = false;
      for (const r of rows) {
        const v = String(r[col] ?? "").trim();
        if (!v) continue;
        if (v.length > 60) long = true;
        seen.add(v.toLowerCase());
        if (seen.size > 40) break;
      }
      const type = !long && seen.size > 0 && seen.size <= 40 ? "select" : "contains";
      return { id: `cfg_${col}`, label: columnLabel(col), type, column: col };
    }),
    groups: [],
  };
}
